import { useWorkspace } from 'components/providers'
import { collection, getCountFromServer, query, where } from 'firebase/firestore'
import { sha256 } from 'js-sha256'
import { isEqual } from 'lodash-es'
import { ILeadLookupConfig, ILeadLookupValue, IWorkspace } from 'types'
import {
  BatchCreateRow,
  BatchDeleteRow,
  BatchRow,
  BatchUpdateRow,
  batchWrite,
  createDoc,
  db,
  updateDoc,
  useDoc,
  useDocs,
} from 'utils'

export async function getLookupValuesCount(lookupId: string): Promise<number> {
  const q = query(collection(db, 'leadLookupValues'), where('configId', '==', lookupId))

  const snapshot = await getCountFromServer(q)
  return snapshot.data().count
}

export function cleanDuplicates(rawDbData: Record<string, string>[], dbInputProps: string[]) {
  const seen = new Set<string>()
  const cleanedData: Record<string, string>[] = []
  for (const session of rawDbData) {
    const key = dbInputProps
      .map(prop => session[prop] ?? '')
      .map(v => v.trim().toLowerCase())
      .join('|')

    if (!seen.has(key)) {
      seen.add(key)
      cleanedData.push(session)
    }
  }

  return {
    cleanedData,
    duplicatesNum: rawDbData.length - cleanedData.length,
  }
}

export function isSmartColumnInput(propsToDelete: string[], workspaceId: string) {
  const [configs] = useDocs('leadLookupConfig', [where('workspaceId', '==', workspaceId)])
  const isInput = configs.some(
    (config: ILeadLookupConfig) =>
      config.status === 'success' &&
      propsToDelete.some(prop => config.inputProperties?.includes(prop))
  )
  return isInput
}

export function getSmartColumnsWithCharts(configId: string) {
  const [content] = useDoc('leadLookupConfig', configId)
  const chartColumns = content?.outputProperties
    ?.filter(prop => prop.hasChart)
    .map(prop => prop.name)
  return chartColumns
}

export function useWorkspaceSmartColumns(workspaceId?: string, withCharts?: boolean) {
  const { workspace } = useWorkspace()
  const id = workspaceId || workspace?.id
  const [configs] = useDocs('leadLookupConfig', id ? [where('workspaceId', '==', id)] : undefined)
  const smartCols = configs.flatMap((config: ILeadLookupConfig) =>
    (config.outputProperties || [])
      .filter(prop => !withCharts || prop.hasChart)
      .map(prop => prop.name)
  )
  return Array.from(new Set(smartCols))
}

export async function createNewSmartColumn(userId: string, workspace: IWorkspace) {
  return createDoc(userId, 'leadLookupConfig', {
    status: 'initialized',
    workspaceId: workspace.id,
  })
}

export async function updateLookUpConfig(
  userId: string,
  lookupId: string,
  newConfig: Partial<ILeadLookupConfig>
) {
  updateDoc(userId, 'leadLookupConfig', {
    id: lookupId,
    ...newConfig,
  })
}

export function parseNewLookupValues(
  rawDbData: Record<string, string>[],
  inputProps: string[],
  dbInputProps: string[],
  outputProps: string[],
  dbOutputProps: string[],
  configId: string,
  workspaceId: string
) {
  const inputName = inputProps.length > 1 ? 'hash' : inputProps[0]
  const batchRows = rawDbData.map(row => {
    const valueFactors = Object.entries(row)
      .filter(([key]) => dbInputProps.includes(key))
      .map(([, value]) => value)
    const inputValue =
      inputProps.length > 1
        ? hashInputValues(valueFactors, workspaceId)
        : String(row[dbInputProps[0]])
    return {
      type: 'create',
      body: {
        configId: configId,
        inputName: inputName,
        inputValue: inputValue,
        outputProperties: [
          ...outputProps.map((prop, index) => ({
            name: prop,
            value: row[dbOutputProps[index]],
          })),
        ],
        workspaceId: workspaceId,
      },
    } as BatchCreateRow<'leadLookupValues'>
  })
  return batchRows
}

export function parseLookupValuesForUpdate(
  rawDbData: Record<string, string>[],
  inputProps: string[],
  dbInputProps: string[],
  outputProps: string[],
  dbOutputProps: string[],
  configId: string,
  workspaceId: string,
  content: ILeadLookupValue[]
) {
  function normalize(val: unknown) {
    return String(val ?? '')
      .trim()
      .toLowerCase()
  }

  const comparisonMap = new Map(content.map(doc => [normalize(doc.inputValue), doc]))
  const rawInputValues: string[] = []

  const creates: BatchCreateRow<'leadLookupValues'>[] = []
  const updates: BatchUpdateRow<'leadLookupValues'>[] = []
  const deletes: BatchDeleteRow[] = []

  for (const row of rawDbData) {
    const valueFactors = Object.entries(row)
      .filter(([key]) => dbInputProps.includes(key))
      .map(([, value]) => value)
    const inputValueRaw =
      inputProps.length > 1
        ? hashInputValues(valueFactors, workspaceId)
        : String(row[dbInputProps[0]])
    const inputValue = normalize(inputValueRaw)
    rawInputValues.push(inputValue)

    const newOutputProperties = outputProps.map((prop, index) => ({
      name: prop,
      value: row[dbOutputProps[index]],
    }))

    const existingDoc = comparisonMap.get(inputValue)

    if (!existingDoc) {
      creates.push({
        type: 'create',
        body: {
          configId,
          inputName: inputProps.length > 1 ? 'hash' : inputProps[0],
          inputValue: inputValueRaw,
          outputProperties: newOutputProperties,
          workspaceId: workspaceId,
        },
      })
    } else {
      const existingOutputProperties = existingDoc.outputProperties || []
      if (!isEqual(existingOutputProperties, newOutputProperties)) {
        const docId = existingDoc.id
        if (!docId) continue

        updates.push({
          type: 'update',
          body: {
            id: docId,
            configId,
            inputName: inputProps.length > 1 ? 'hash' : inputProps[0],
            inputValue: inputValueRaw,
            outputProperties: newOutputProperties,
          },
        })
      }
    }
  }

  for (const doc of content) {
    const normalizedInputValue = normalize(doc.inputValue)
    const uniqueRawInputValues = new Set(rawInputValues)
    if (!uniqueRawInputValues.has(normalizedInputValue) && doc.id) {
      deletes.push({
        type: 'delete',
        body: { id: doc.id },
      })
    }
  }

  const batchRows = [...creates, ...updates, ...deletes]

  return {
    batchRows,
    newRows: creates.length,
    updatedRows: updates.length,
    deletedRows: deletes.length,
  }
}

export async function updateLookupValues(
  userId: string,
  batchRows: BatchRow<'leadLookupValues'>[],
  setProgress: (progress: number) => void,
  setError: (error: string) => void,
  abortSignal: AbortSignal,
  cleanup?: () => void
) {
  try {
    await batchWrite(userId, 'leadLookupValues', batchRows, setProgress, abortSignal, cleanup)
  } catch (error) {
    console.error(error)
    setProgress(0)
    if (!(error as Error)?.message?.includes('Batch write aborted'))
      setError('Error updating smart column values')
    else if (!(error as Error)?.message?.includes('running cleanup')) {
      setError('Update for values was cancelled but some values were updated')
    }
  }
}

function hashInputValues(inputValues: string[], workspaceId: string): string {
  const value = canonicalize(inputValues)
  const encoder = new TextEncoder()
  const keyBytes = encoder.encode(workspaceId.normalize('NFC'))
  const valueBytes = encoder.encode(value.normalize('NFC'))
  const hash = sha256.hmac(keyBytes, valueBytes)
  return hash
}

function canonicalize(values: string[]): string {
  return values
    .map(v => v.normalize('NFC').trim().toLowerCase())
    .sort()
    .join('|')
}
